import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ThemeProvider } from '@mui/material/styles'
import { heapEvent } from '@equipmentshare/heaper'
import {
	PickOrderDetailFragment,
	PickOrderStatus,
	useCancelPickOrderMutation,
	useCompletePickOrderMutation,
	usePickOrderQuery,
	useRecordPickedQuantitiesMutation,
} from '../../../graphql'
import { FileCopyLineIcon } from '../../../components/icons'
import { pickFlowTheme } from '../../../config/muiTheme'
import { useShowError, useShowSuccess } from '../../../hooks/useToast'
import { copyToClipboard } from '../../../utils/clipboard'
import { singleLineAddress } from './address'
import { PICK_ORDER_PREFIX, PICK_ORDER_STATUS_CHIP_COLOR, PICK_ORDER_STATUS_LABEL, PICK_ORDER_STEPS } from './constants'
import {
	DraftCarton,
	ShortfallDisposition,
	ShortfallDispositions,
	activeLines,
	cancelledLines,
	hasReportableOutcome,
	initialPickedQuantities,
	leftOpenLines,
	shortPickedLines,
	toCompleteInput,
	toRecordPickedInput,
	totalUnpacked,
	unpackChangedLines,
} from './draft'
import { fulfillmentChain } from './chain'
import {
	PickEntries,
	ProductId,
	initialPickEntries,
	pickItems,
	pickedFromEntries,
	unenteredItems,
} from './picking'
import FulfillmentChainLinks from './components/FulfillmentChainLinks'
import AddOrdersToPickOrderControl from './components/AddOrdersToPickOrderControl'
import AssignPickOrderControl from './components/AssignPickOrderControl'
import CancelPickOrderButton from './components/CancelPickOrderButton'
import CancelledLinesNotice from './components/CancelledLinesNotice'
import CompletedRunSummary from './components/CompletedRunSummary'
import CompleteStep from './components/CompleteStep'
import FinalizePickOrderButton from './components/FinalizePickOrderButton'
import PackStep from './components/PackStep'
import PickStep from './components/PickStep'
import { userDisplayName } from './userDisplayName'
import PackingSlipsDocument from './print/PackingSlipsDocument'
import PickListDocument from './print/PickListDocument'
import PrintPortal from './print/PrintPortal'
import { usePrintJob } from './print/usePrintJob'

/** Where the Pick step starts for a run that has just loaded. See `initialPickEntries`. */
function seedEntries( lines: PickOrderDetailFragment['lines'], hasSavedCounts: boolean ): PickEntries {
	const active = activeLines( lines )
	return initialPickEntries( pickItems( active, initialPickedQuantities( active ) ), hasSavedCounts )
}

let cartonKeySequence = 0
function nextCartonKey(): string {
	cartonKeySequence += 1
	return `carton-${cartonKeySequence}`
}

export default function PickOrderDetails() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const showSuccess = useShowSuccess()
	const showError = useShowError()

	const { data, loading, error } = usePickOrderQuery( {
		variables: { pickOrderId: id ?? '' },
		skip: !id,
		fetchPolicy: 'cache-and-network',
	} )
	const pickOrder = data?.pickOrder

	const [ activeStep, setActiveStep ] = useState( 0 )
	// What the picker has typed, per product. Blank until they type, and only
	// turned into per-line counts — and saved — when they continue to pack.
	const [ entries, setEntries ] = useState<PickEntries>( () => seedEntries( pickOrder?.lines ?? [], !!pickOrder?.datePickingStarted ) )
	// The counts as of the last "Continue to pack", which Pack and Review work
	// from. recordPickedQuantities takes absolute counts and is safe to repeat,
	// so going back and continuing again is never a partial write.
	const [ picked, setPicked ] = useState( () => initialPickedQuantities( pickOrder?.lines ?? [] ) )
	// Products left blank when the picker last continued, for the Pack step's warning.
	const [ unenteredProductIds, setUnenteredProductIds ] = useState<ProductId[]>( [] )
	const [ cartons, setCartons ] = useState<DraftCarton[]>( [] )
	// Only one carton is open on the Pack step; this is the one being filled.
	const [ activeCartonKey, setActiveCartonKey ] = useState<string | null>( null )
	// Lines taken out of every carton the last time the picker continued, because
	// their counts changed. Said on the Pack step so the repacking is no surprise.
	const [ unpackedLineCount, setUnpackedLineCount ] = useState( 0 )
	// What happens to the units a line came up short on. Empty means every short
	// line is left open for the missing units; the Review step is where a line
	// can be shorted instead.
	const [ dispositions, setDispositions ] = useState<ShortfallDispositions>( {} )
	// Which pick order the local counts belong to, so they are re-seeded when the
	// query resolves and again if the route moves to a different pick order.
	const [ seededPickOrderId, setSeededPickOrderId ] = useState( () => pickOrder?.id )

	const [ completePickOrder, { loading: isCompleting } ] = useCompletePickOrderMutation()
	const [ recordPickedQuantities, { loading: isRecording } ] = useRecordPickedQuantitiesMutation()
	const [ cancelPickOrder, { loading: isCancelling } ] = useCancelPickOrderMutation()
	const { job: printJob, print } = usePrintJob<'pickList' | 'packingSlips'>()

	const lines = pickOrder?.lines ?? []
	const workable = activeLines( lines )
	const pickableItems = pickItems( workable, picked )

	// The query can resolve after the first render, so seed during render rather
	// than in an effect — this is derived state, not an external system.
	if ( pickOrder && pickOrder.id !== seededPickOrderId ) {
		setSeededPickOrderId( pickOrder.id )
		setPicked( initialPickedQuantities( pickOrder.lines ) )
		setEntries( seedEntries( pickOrder.lines, !!pickOrder.datePickingStarted ) )
		setUnenteredProductIds( [] )
		setUnpackedLineCount( 0 )
		setCartons( [] )
		setActiveCartonKey( null )
		setActiveStep( 0 )
		setDispositions( {} )
	}

	const handleEntryChange = ( productId: ProductId, quantity: number | null ) => {
		setEntries( ( prev ) => {
			const next = { ...prev }
			if ( quantity === null ) {
				delete next[ productId ]
			} else {
				next[ productId ] = quantity
			}
			return next
		} )
	}

	const handleDispositionChange = ( lineId: string, disposition: ShortfallDisposition ) => {
		setDispositions( ( prev ) => ( { ...prev, [ lineId ]: disposition } ) )
	}

	// A new carton is always the one the packer wants to fill next, so opening it
	// is part of adding it.
	const handleAddCarton = () => {
		const key = nextCartonKey()
		setCartons( ( prev ) => [ ...prev, { key, trackingNumber: '', contents: {} } ] )
		setActiveCartonKey( key )
	}

	const handleRemoveCarton = ( key: string ) => {
		const index = cartons.findIndex( ( carton ) => carton.key === key )
		const next = cartons.filter( ( carton ) => carton.key !== key )
		setCartons( next )
		if ( key === activeCartonKey ) {
			// Land on a neighbour rather than on nothing, so packing carries on.
			setActiveCartonKey( ( next[ index ] ?? next[ index - 1 ] )?.key ?? null )
		}
	}

	// Allocating a product spreads it over several lines at once, so cartons take a
	// set of absolute quantities rather than one line at a time.
	const handleCartonContentsChange = ( key: string, contents: Record<string, number> ) => {
		setCartons( ( prev ) => prev.map( ( carton ) => (
			carton.key === key ? { ...carton, contents: { ...carton.contents, ...contents } } : carton
		) ) )
	}

	const handleTrackingNumberChange = ( key: string, trackingNumber: string ) => {
		setCartons( ( prev ) => prev.map( ( carton ) => (
			carton.key === key ? { ...carton, trackingNumber } : carton
		) ) )
	}

	const goToPack = async() => {
		if ( !pickOrder ) {
			return
		}
		// A blank is a zero from here on.
		const nextPicked = pickedFromEntries( pickableItems, entries )
		// Persist before moving on, so a tablet that sleeps or a picker who walks
		// away does not lose the count. A failure here is worth stopping for: the
		// alternative is packing against numbers the server does not have.
		try {
			const result = await recordPickedQuantities( {
				variables: { input: toRecordPickedInput( pickOrder.id, lines, nextPicked ) },
			} )
			const recorded = result.data?.recordPickedQuantities
			if ( !recorded?.success ) {
				showError( `recordPicked-${pickOrder.id}`, recorded?.message ?? 'Could not save the picked counts.' )
				return
			}
		} catch ( err ) {
			showError( `recordPicked-${pickOrder.id}`, err instanceof Error ? err.message : 'Could not save the picked counts.' )
			return
		}

		// Committing the Pick step lands new counts on every line at once. A line
		// whose count changed comes out of its cartons to be packed again.
		const unpacking = unpackChangedLines( cartons, picked, nextPicked )
		setPicked( nextPicked )
		setCartons( unpacking.cartons )
		setUnpackedLineCount( unpacking.unpackedLineIds.length )
		setUnenteredProductIds( unenteredItems( pickableItems, entries ).map( ( item ) => item.productId ) )

		// Opening the Pack step with no cartons would show an empty screen; start
		// the packer off with one.
		if ( unpacking.cartons.length === 0 ) {
			handleAddCarton()
		} else if ( !unpacking.cartons.some( ( carton ) => carton.key === activeCartonKey ) ) {
			setActiveCartonKey( unpacking.cartons[ 0 ].key )
		}
		setActiveStep( 1 )
	}

	const handleCancel = async() => {
		if ( !pickOrder ) {
			return
		}
		try {
			const result = await cancelPickOrder( { variables: { input: { pickOrderId: pickOrder.id } } } )
			const cancelled = result.data?.cancelPickOrder
			if ( !cancelled?.success ) {
				showError( `cancelPickOrder-${pickOrder.id}`, cancelled?.message ?? 'Could not cancel the pick order.' )
				return
			}
			heapEvent( 'Inventory - DC Admin - Pick Order - Cancel - Success', { pick_order_id: pickOrder.id } )
			showSuccess( `cancelPickOrder-${pickOrder.id}`, `Pick order ${PICK_ORDER_PREFIX}${pickOrder.pickOrderNumber} cancelled.` )
			navigate( '/dc-admin/pick-orders' )
		} catch ( err ) {
			showError( `cancelPickOrder-${pickOrder.id}`, err instanceof Error ? err.message : 'Could not cancel the pick order.' )
		}
	}

	const handleCopyAddress = async() => {
		if ( !pickOrder?.shippingAddress ) {
			return
		}
		const result = await copyToClipboard( singleLineAddress( pickOrder.shippingAddress ) )
		if ( result.success ) {
			showSuccess( `copyAddress-${pickOrder.id}`, 'Address copied to clipboard' )
		} else {
			showError( `copyAddress-${pickOrder.id}`, 'Could not copy the address.' )
		}
	}

	const handleComplete = async() => {
		if ( !pickOrder ) {
			return
		}
		const input = toCompleteInput( pickOrder.id, lines, picked, cartons, dispositions )
		try {
			const result = await completePickOrder( { variables: { input } } )
			const completed = result.data?.completePickOrder
			if ( !completed?.success ) {
				heapEvent( 'Inventory - DC Admin - Pick Order - Complete - Submit - Error', { pick_order_id: pickOrder.id } )
				showError( `completePickOrder-${pickOrder.id}`, completed?.message ?? 'Could not complete the pick order.' )
				return
			}
			const fulfillmentCount = completed.pickOrder?.fulfillments.length ?? 0
			heapEvent( 'Inventory - DC Admin - Pick Order - Complete - Submit - Success', {
				pick_order_id: pickOrder.id,
				carton_count: input.cartons.length,
				short_pick_count: shortPickedLines( lines, picked ).length,
				left_open_count: leftOpenLines( lines, picked, dispositions ).length,
				tracking_number_count: input.cartons.filter( ( carton ) => !!carton.trackingNumber ).length,
			} )
			showSuccess(
				`completePickOrder-${pickOrder.id}`,
				// Every order in the run can be cancelled out from under the picker,
				// in which case the run closes as cancelled with nothing shipped.
				fulfillmentCount === 0
					? `Pick order ${PICK_ORDER_PREFIX}${pickOrder.pickOrderNumber} closed — ${completed.message}`
					: `${PICK_ORDER_PREFIX}${pickOrder.pickOrderNumber} finalized.`,
			)
			// Stay on the run, which now shows what shipped and offers the packing
			// slips — the transfer order numbers on them only exist from here on.
		} catch ( err ) {
			heapEvent( 'Inventory - DC Admin - Pick Order - Complete - Submit - Error', { pick_order_id: pickOrder.id } )
			showError( `completePickOrder-${pickOrder.id}`, err instanceof Error ? err.message : 'Could not complete the pick order.' )
		}
	}

	if ( loading && !pickOrder ) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
				<CircularProgress />
			</Box>
		)
	}

	if ( error || !pickOrder ) {
		return (
			<Box sx={{ p: 3 }}>
				<Alert
					severity="error"
					action={
						<Button color="inherit" size="small" component={Link} to="/dc-admin/pick-orders">
							Back to Pick Orders
						</Button>
					}
				>
					{error?.message ?? 'Pick order not found.'}
				</Alert>
			</Box>
		)
	}

	const isComplete = pickOrder.status === PickOrderStatus.Completed
	// An MTT loadout has no address to ship to, and one with no street is no use pasted anywhere.
	const shippingAddress = pickOrder.shippingAddress?.line1 ? pickOrder.shippingAddress : null
	const isCancelled = pickOrder.status === PickOrderStatus.Cancelled
	const isClosed = isComplete || isCancelled
	const dropped = cancelledLines( lines )
	const unpacked = totalUnpacked( lines, picked, cartons )
	const leftOpen = leftOpenLines( lines, picked, dispositions )
	const canComplete = hasReportableOutcome( lines, picked, cartons, dispositions )
	const unentered = pickableItems.filter( ( item ) => unenteredProductIds.includes( item.productId ) )
	const enteredCount = pickableItems.length - unenteredItems( pickableItems, entries ).length

	return (
		<ThemeProvider theme={pickFlowTheme}>
			<Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
				<Stack spacing={2.5}>
					<Box>
						<Link to="/dc-admin/pick-orders">Pick Orders</Link>
						<Stack
							direction={{ xs: 'column', sm: 'row' }}
							spacing={1.5}
							alignItems={{ xs: 'flex-start', sm: 'center' }}
							sx={{ mt: 1 }}
						>
							<Typography variant="h4">{PICK_ORDER_PREFIX}{pickOrder.pickOrderNumber}</Typography>
							<Chip
								label={PICK_ORDER_STATUS_LABEL[ pickOrder.status ] ?? pickOrder.status}
								color={PICK_ORDER_STATUS_CHIP_COLOR[ pickOrder.status ] ?? 'default'}
							/>
							{!isClosed && (
								<Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
									<AddOrdersToPickOrderControl
										pickOrderId={pickOrder.id}
										memberFulfillmentOrderId={pickOrder.fulfillmentOrders[ 0 ]?.id ?? null}
										disabled={isCancelling || isCompleting}
									/>
									<CancelPickOrderButton
										pickOrderNumber={pickOrder.pickOrderNumber}
										disabled={isCancelling || isCompleting}
										onConfirm={handleCancel}
									/>
								</Box>
							)}
						</Stack>
						{/* Who is picking sits in this line rather than on a surface of
						    its own: the assignee is already named here, and reassigning
						    is rare enough that it should not outweigh the picking below. */}
						<Stack
							direction="row"
							alignItems="center"
							useFlexGap
							flexWrap="wrap"
							sx={{ mt: 0.5, columnGap: 0.5, typography: 'body2', color: 'text.secondary' }}
						>
							<Box component="span">{pickOrder.destinationDescription ?? 'No destination'}</Box>
							{shippingAddress && (
								<Tooltip title="Copy address">
									<IconButton size="small" aria-label="Copy address" onClick={handleCopyAddress} sx={{ p: 0.25 }}>
										<FileCopyLineIcon fontSize="inherit" />
									</IconButton>
								</Tooltip>
							)}
							<Box component="span">·</Box>
							{isClosed ? (
								<Box component="span">{userDisplayName( pickOrder.assignedToUser ) ?? 'Unassigned'}</Box>
							) : (
								<AssignPickOrderControl
									pickOrderId={pickOrder.id}
									assignedToUser={pickOrder.assignedToUser}
									disabled={isCancelling || isCompleting}
								/>
							)}
							<Box component="span">·</Box>
							<FulfillmentChainLinks chain={fulfillmentChain( pickOrder )} />
						</Stack>
					</Box>

					{dropped.length > 0 && <CancelledLinesNotice lines={dropped} />}

					{isCancelled && (
						<Alert severity="info">
							This pick order was cancelled.
						</Alert>
					)}

					{isComplete && (
						<CompletedRunSummary pickOrder={pickOrder} onPrintPackingSlips={() => print( 'packingSlips', `${PICK_ORDER_PREFIX}${pickOrder.pickOrderNumber}-packing-slips` )} />
					)}

					{!isClosed && (
						<>
							<Stepper activeStep={activeStep} alternativeLabel>
								{PICK_ORDER_STEPS.map( ( step ) => (
									<Step key={step}>
										<StepLabel>{step}</StepLabel>
									</Step>
								) )}
							</Stepper>

							{activeStep === 0 && (
								<PickStep
									items={pickableItems}
									entries={entries}
									onEntryChange={handleEntryChange}
									onPrintPickList={() => print( 'pickList', `${PICK_ORDER_PREFIX}${pickOrder.pickOrderNumber}-pick-list` )}
								/>
							)}
							{activeStep === 1 && unentered.length > 0 && (
								<Alert severity="warning">
									Left blank, recorded as 0 picked: {unentered.map( ( item ) => item.identity.sku ?? item.identity.description ).join( ', ' )}
								</Alert>
							)}
							{activeStep === 1 && unpackedLineCount > 0 && (
								<Alert severity="info" onClose={() => setUnpackedLineCount( 0 )}>
									{unpackedLineCount === 1
										? '1 line was unpacked because its count changed.'
										: `${unpackedLineCount} lines were unpacked because their counts changed.`}
								</Alert>
							)}
							{activeStep === 1 && (
								<PackStep
									lines={workable}
									picked={picked}
									cartons={cartons}
									activeCartonKey={activeCartonKey}
									onActiveCartonChange={setActiveCartonKey}
									onAddCarton={handleAddCarton}
									onRemoveCarton={handleRemoveCarton}
									onCartonContentsChange={handleCartonContentsChange}
									onTrackingNumberChange={handleTrackingNumberChange}
								/>
							)}
							{activeStep === 2 && (
								<CompleteStep
									lines={workable}
									picked={picked}
									cartons={cartons}
									dispositions={dispositions}
									onDispositionChange={handleDispositionChange}
								/>
							)}

							{/* Step footer — the only way forward or back, kept in one place so
							    the picker never hunts for it. */}
							<Paper
								variant="outlined"
								sx={{
									p: 2,
									position: 'sticky',
									bottom: 0,
									borderColor: 'pickFlow.cardBorder',
									bgcolor: 'pickFlow.surface',
								}}
							>
								<Stack
									direction={{ xs: 'column', sm: 'row' }}
									spacing={1.5}
									justifyContent="space-between"
									alignItems={{ xs: 'stretch', sm: 'center' }}
								>
									<Typography variant="body2" color="text.secondary">
										{activeStep === 0 && `${enteredCount} of ${pickableItems.length} products entered`}
										{activeStep === 1 && ( unpacked > 0 ? `${unpacked} ${unpacked === 1 ? 'unit' : 'units'} left to pack` : 'Everything is packed' )}
										{activeStep === 2 && (
											leftOpen.length > 0
												? `${leftOpen.length} short ${leftOpen.length === 1 ? 'line stays' : 'lines stay'} open`
												: ''
										)}
									</Typography>
									<Stack direction="row" spacing={1.5}>
										{activeStep > 0 && (
											<Button color="inherit" onClick={() => setActiveStep( ( prev ) => prev - 1 )}>
												Back
											</Button>
										)}
										{activeStep === 0 && (
											<Button
												variant="contained"
												onClick={goToPack}
												disabled={isRecording}
												startIcon={isRecording ? <CircularProgress size={16} color="inherit" /> : undefined}
											>
												Continue to pack
											</Button>
										)}
										{activeStep === 1 && (
											<Button variant="contained" onClick={() => setActiveStep( 2 )}>Review</Button>
										)}
										{activeStep === 2 && (
											<FinalizePickOrderButton
												unpacked={unpacked}
												onConfirm={handleComplete}
												// Nothing shipping and nothing shorted leaves inventory-api
												// no carton to record, which it rejects. The Review step
												// says so and points at cancelling instead.
												disabled={isCompleting || !canComplete}
												isCompleting={isCompleting}
											/>
										)}
									</Stack>
								</Stack>
							</Paper>
						</>
					)}
				</Stack>
			</Box>

			{printJob === 'pickList' && (
				<PrintPortal><PickListDocument pickOrder={pickOrder} items={pickableItems} /></PrintPortal>
			)}
			{printJob === 'packingSlips' && (
				<PrintPortal><PackingSlipsDocument pickOrder={pickOrder} /></PrintPortal>
			)}
		</ThemeProvider>
	)
}
